import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderSortButton,
  TableRow,
} from '../../../components/organisms/Table';
import { isSharedFile, SHARED_FILES_TABLE_ID, SharedDirectory, SharedFile } from '../SharedFiles.types';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { SharedFilesTableRowDirectory, SharedFilesTableRowFile } from './SharedFilesTableRows';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import { useWebsocketSyncedAttachments } from '../../../_lib/components/AttachedFilesBase';
import { SharedFilesSortType, useSharedFilesSort } from '../hooks/useSharedFilesSort';
import { SkipLink, SkipLinkTarget } from '../../../_lib/components/SkipLink';
import { SharedFilesGlobalCheckbox } from './SharedFilesCheckboxes';

type SharedFilesTable = {
  files?: SharedFile[];
  directories?: SharedDirectory[];
};

/**
 * Shared files Table.
 * Displaying a Files and Directories.
 */
export function SharedFilesTable({ files = [], directories = [] }: SharedFilesTable) {
  const { t } = useSonTranslation('SharedFiles');
  const id = useHtmlValidId();

  const { getSortOrderByType, sortBy, sortByName, sortBySize, sortByFileType, sortByEditDate, sortByEditedBy } =
    useSharedFilesSort();

  // sync files with Websockets
  const { syncedAttachments: syncedSharedFiles } = useWebsocketSyncedAttachments(files);

  const showSkipLinks = syncedSharedFiles?.length >= 3;

  const filesAndDirs = [...syncedSharedFiles, ...directories];

  const sortedFilesAndDirs = filesAndDirs
    .sort(sortBy)
    .map(item =>
      isSharedFile(item) ? (
        <SharedFilesTableRowFile key={item.id} file={item} />
      ) : (
        <SharedFilesTableRowDirectory key={item.pageLink.url} dir={item} />
      )
    );

  return (
    <>
      {showSkipLinks && <SkipLink id={id}>{t('skiplinkToBottom')}</SkipLink>}
      <Table customTableClass="flexible-tables__table" caption={t('table.caption')} id={SHARED_FILES_TABLE_ID}>
        <TableHead customTableHeadClass="flexible-tables__tablehead">
          <TableRow className="flexible-tables__tablerow">
            <TableCell customTableHeaderClass="flexible-tables__tableheader">
              <SharedFilesGlobalCheckbox />
            </TableCell>
            <TableHeaderSortButton
              onClick={sortByName}
              label={t('table.head.name')}
              ariaSort={getSortOrderByType(SharedFilesSortType.Name)}
              customTableHeaderClass="flexible-tables__tableheader"
            />
            <TableHeaderSortButton
              onClick={sortByFileType}
              label={t('table.head.fileType')}
              ariaSort={getSortOrderByType(SharedFilesSortType.FileType)}
              customTableHeaderClass="flexible-tables__tableheader"
            />
            <TableHeaderSortButton
              onClick={sortBySize}
              label={t('table.head.fileSize')}
              ariaSort={getSortOrderByType(SharedFilesSortType.Size)}
              customTableHeaderClass="flexible-tables__tableheader"
            />
            <TableHeaderSortButton
              onClick={sortByEditDate}
              label={t('table.head.editDate')}
              ariaSort={getSortOrderByType(SharedFilesSortType.EditDate)}
              customTableHeaderClass="flexible-tables__tableheader"
            />
            <TableHeaderSortButton
              onClick={sortByEditedBy}
              label={t('table.head.editedBy')}
              ariaSort={getSortOrderByType(SharedFilesSortType.EditedBy)}
              customTableHeaderClass="flexible-tables__tableheader"
            />
            <TableCell customTableHeaderClass="flexible-tables__tableheader">{t('table.head.action')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody className="flexible-tables__tablebody">{sortedFilesAndDirs}</TableBody>
      </Table>
      {showSkipLinks && (
        <>
          <SkipLinkTarget targetName={t('skiplinkBottomName')} id={id} />
          <div className="shared-files__skip-link-container">
            <SkipLink id={SHARED_FILES_TABLE_ID}>{t('skiplinkToTop')}</SkipLink>
          </div>
        </>
      )}
    </>
  );
}
